<template>
  <div class="training-plan-guide-page">
    <CommonHeader />

    <div class="guide-content">
      <aside class="plan-sidebar">
        <div class="sidebar-title">我的培训计划</div>
        <div class="plan-list" v-loading="loadingPlans">
          <div
            v-for="plan in planList"
            :key="plan.id"
            :class="['plan-item', { active: activePlanId === plan.id }]"
            @click="selectPlan(plan.id)"
          >
            <div class="plan-name">{{ plan.planName }}</div>
            <div class="plan-meta">
              <span>第 {{ plan.dayIndex || 0 }} 天</span>
              <span>{{ getPlanStatusLabel(plan) }}</span>
            </div>
          </div>
          <el-empty
            v-if="!loadingPlans && planList.length === 0"
            description="暂无已发布培训计划"
            :image-size="90"
          ></el-empty>
        </div>
      </aside>

      <main class="plan-detail">
        <div class="plan-detail-body" v-loading="loadingDetail">
        <template v-if="planDetail">
          <div class="detail-header">
            <div>
              <h2 class="detail-title">{{ planDetail.planName }}</h2>
              <p class="detail-desc">{{ planDetail.planDesc || '暂无计划说明' }}</p>
            </div>
            <div class="detail-metrics">
              <div class="metric-card">
                <div class="metric-label">起始时间</div>
                <div class="metric-value">{{ planDetail.planStartTime || '-' }}</div>
              </div>
              <div class="metric-card">
                <div class="metric-label">截止时间</div>
                <div class="metric-value">{{ planDetail.planDeadlineTime || '不限时' }}</div>
              </div>
              <div class="metric-card">
                <div class="metric-label">当前天数</div>
                <div class="metric-value">第 {{ planDetail.dayIndex || 0 }} 天</div>
              </div>
              <div class="metric-card">
                <div class="metric-label">当前阶段</div>
                <div class="metric-value">{{ planDetail.currentStageName || '待开始' }}</div>
              </div>
              <div class="metric-card">
                <div class="metric-label">计划状态</div>
                <div class="metric-value">{{ getPlanStatusLabel(planDetail) }}</div>
              </div>
            </div>
          </div>

          <el-alert
            v-if="isPlanExpired(planDetail)"
            class="plan-deadline-alert"
            type="error"
            :closable="false"
            :title="getPlanExpiredTip(planDetail)"
          ></el-alert>

          <section class="guide-stats">
            <div class="guide-stat-card guide-stat-card--actionable">
              <div class="guide-stat-card__label">可立即完成</div>
              <div class="guide-stat-card__value">{{ pendingTaskCount }}</div>
            </div>
            <div class="guide-stat-card guide-stat-card--completed">
              <div class="guide-stat-card__label">已完成</div>
              <div class="guide-stat-card__value">{{ completedTaskCount }}</div>
            </div>
            <div class="guide-stat-card guide-stat-card--locked">
              <div class="guide-stat-card__label">待解锁</div>
              <div class="guide-stat-card__value">{{ lockedTaskCount }}</div>
            </div>
            <div class="guide-stat-card guide-stat-card--total">
              <div class="guide-stat-card__label">任务总数</div>
              <div class="guide-stat-card__value">{{ totalTaskCount }}</div>
            </div>
          </section>

          <div class="stage-list">
            <div
              v-for="stage in planDetail.stageList"
              :key="stage.id"
              class="stage-card"
            >
              <div class="stage-head">
                <div>
                  <div class="stage-title">
                    阶段{{ stage.stageOrder }}：{{ stage.stageName }}
                  </div>
                  <div class="stage-sub">
                    第 {{ stage.unlockDay }} 天开放 · 类型：{{ getStageTypeLabel(stage.stageType) }}
                  </div>
                </div>
                <div class="stage-tags">
                  <el-tag size="small" :type="getStageStatusType(stage.stageStatus)">
                    {{ getStageStatusLabel(stage.stageStatus) }}
                  </el-tag>
                  <el-tag
                    v-if="hasExamAttempt(stage)"
                    size="small"
                    type="info"
                  >
                    阶段分数：{{ formatStageExamScore(stage) }}
                  </el-tag>
                  <el-tag
                    v-if="hasExamAttempt(stage)"
                    size="small"
                    :type="isStageExamPendingReview(stage) ? 'warning' : 'success'"
                  >
                    {{ formatStageExamReviewStatus(stage) }}
                  </el-tag>
                </div>
              </div>

              <div v-if="stage.lockReason" class="lock-reason">
                <i class="el-icon-lock"></i>
                {{ stage.lockReason }}
              </div>

              <div class="material-block">
                <div class="block-title">学习资料</div>
                <div v-if="!stage.materialList || stage.materialList.length === 0" class="block-empty">
                  该阶段未配置学习资料
                </div>
                <div v-else class="material-list">
                  <div
                    v-for="materialRel in stage.materialList"
                    :key="materialRel.id"
                    :class="['material-item', { highlight: isTargetMaterial(materialRel) }]"
                  >
                    <div class="material-info">
                      <div class="material-title">
                        {{ getMaterialTitle(materialRel) }}
                      </div>
                      <div class="material-meta">
                        <span>{{ getMaterialType(materialRel) }}</span>
                        <span v-if="materialRel.requiredFlag !== '0'" class="required-mark">必学</span>
                      </div>
                    </div>
                    <div class="material-actions">
                      <el-button
                        size="mini"
                        type="primary"
                        plain
                        :disabled="isMaterialActionDisabled(stage, materialRel)"
                        @click="previewMaterial(stage, materialRel)"
                      >
                        开始学习
                      </el-button>
                      <el-tag v-if="materialRel.userStatus === '1'" size="small" type="success">已完成</el-tag>
                      <el-tag v-else size="small" type="info">学习中</el-tag>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="stage.examId" class="exam-block">
                <div class="block-title">阶段考试</div>
                <div class="exam-row">
                  <span>{{ stage.examName || '未命名考试' }}</span>
                  <el-button
                    size="mini"
                    type="warning"
                    :disabled="isExamActionDisabled(stage)"
                    @click="toExam(stage)"
                  >
                    {{ getExamActionText(stage) }}
                  </el-button>
                </div>
                <div v-if="hasExamAttempt(stage)" class="exam-review-tip">
                  当前成绩：{{ formatStageExamScore(stage) }}，{{ formatStageExamReviewStatus(stage) }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <el-empty
          v-else-if="!loadingDetail"
          description="请选择培训计划"
          :image-size="110"
        ></el-empty>
        </div>
      </main>
    </div>

    <el-dialog
      title="文档学习监控"
      :visible.sync="docDialogVisible"
      fullscreen
      append-to-body
      custom-class="pdf-fullscreen-dialog"
      :close-on-click-modal="false"
      @close="handleDocDialogClose"
    >
      <div class="doc-header-tip">
        <div class="doc-tip-main">
          <i class="el-icon-info"></i>
          <span>{{ docGuideText }}</span>
        </div>
        <div class="doc-progress-tags">
          <span :class="['doc-progress-chip', { done: docRemainingSeconds === 0 }]">
            停留：{{ docStaySeconds }}s / {{ docMinStaySeconds }}s
          </span>
          <span :class="['doc-progress-chip', { done: docReachedBottom }]">
            文档末尾：{{ docReachedBottom ? '已到达' : '未到达' }}
          </span>
        </div>
      </div>
      <div class="doc-viewer-wrap">
        <PdfScrollViewer
          v-if="docDialogVisible"
          :pdf-url="currentPdfUrl"
          :active="docDialogVisible"
          @status-change="handlePdfViewerStatus"
        />
      </div>
      <div class="doc-bottom-confirm">
        <span class="doc-bottom-confirm-tip">{{ docBottomMonitorText }}</span>
      </div>
      <div slot="footer">
        <el-button size="mini" @click="docDialogVisible = false">关闭</el-button>
        <el-button
          size="mini"
          type="primary"
          class="doc-finish-btn"
          :disabled="docCompleting"
          :loading="docCompleting"
          @click="finishDocLearning"
        >
          {{ docFinishButtonText }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import CommonHeader from '@/components/header/CommonHeader.vue'
import PdfScrollViewer from '@/components/training/PdfScrollViewer.vue'
import { completeTrainingMaterial, getMyTrainingPlanDetail, getMyTrainingPlanList, recordApeTrainingMaterialView } from '@/api/api'

export default {
  name: 'TrainingPlanGuide',
  components: {
    CommonHeader,
    PdfScrollViewer
  },
  data() {
    return {
      loadingPlans: false,
      loadingDetail: false,
      planList: [],
      activePlanId: '',
      planDetail: null,
      selectedMaterialId: '',
      docDialogVisible: false,
      currentPdfUrl: '',
      docMinStaySeconds: 30,
      docStaySeconds: 0,
      docReachedBottom: false,
      docStayTimer: null,
      docBottomObserver: null,
      docBottomCheckReady: false,
      docBottomCheckTimer: null,
      pdfLoading: false,
      pdfRenderError: '',
      docCompleting: false,
      autoOpenedTargetKey: '',
      docLearningContext: {
        planId: '',
        stageId: '',
        materialId: ''
      }
    }
  },
  computed: {
    docRemainingSeconds() {
      return Math.max(this.docMinStaySeconds - this.docStaySeconds, 0)
    },
    docGuideText() {
      if (this.pdfLoading || !this.docBottomCheckReady) {
        return '文档加载中，请稍候。系统会自动监控阅读进度。'
      }
      if (this.pdfRenderError) {
        return this.pdfRenderError
      }
      if (this.docRemainingSeconds > 0 && !this.docReachedBottom) {
        return `请继续滚动阅读到文档末尾，并再停留 ${this.docRemainingSeconds} 秒。`
      }
      if (this.docRemainingSeconds > 0) {
        return `已到达文档末尾，请继续停留 ${this.docRemainingSeconds} 秒后完成学习。`
      }
      if (!this.docReachedBottom) {
        return '停留时长已满足，请继续阅读到文档末尾，系统会自动完成检测。'
      }
      return '已满足学习完成条件，现在可以点击“完成学习”。'
    },
    docBottomMonitorText() {
      if (this.pdfLoading || !this.docBottomCheckReady) {
        return '正在初始化 PDF 阅读监控，请稍候...'
      }
      if (this.pdfRenderError) {
        return this.pdfRenderError
      }
      if (this.docReachedBottom) {
        return '系统已自动检测到你滚动到文档末尾，无需手动勾选。'
      }
      return '系统正在自动监控 PDF 页面滚动，阅读到末尾后会自动完成检测。'
    },
    docFinishButtonText() {
      if (this.docCompleting) {
        return '完成学习'
      }
      if (this.pdfLoading || !this.docBottomCheckReady) {
        return '完成学习（文档加载中）'
      }
      if (this.docRemainingSeconds > 0) {
        return `完成学习（${this.docRemainingSeconds}s）`
      }
      if (!this.docReachedBottom) {
        return '完成学习（等待自动检测到底）'
      }
      return '完成学习'
    },
    totalTaskCount() {
      return this.getPlanTaskStats().total
    },
    completedTaskCount() {
      return this.getPlanTaskStats().completed
    },
    pendingTaskCount() {
      return this.getPlanTaskStats().pending
    },
    lockedTaskCount() {
      return this.getPlanTaskStats().locked
    }
  },  mounted() {
    this.selectedMaterialId = this.$route.query.materialId || ''
    this.loadPlanList()
  },
  beforeDestroy() {
    this.clearDocStayTimer()
    this.clearDocBottomCheckTimer()
  },
  watch: {
    '$route.query.materialId'(val) {
      this.selectedMaterialId = val || ''
      this.autoOpenedTargetKey = ''
      this.tryAutoOpenTargetMaterial()
    },
    '$route.query.autoOpen'(val) {
      if (val === '1') {
        this.autoOpenedTargetKey = ''
        this.tryAutoOpenTargetMaterial()
      }
    },
    docDialogVisible(val) {
      if (val) {
        this.startDocStayTimer()
      } else {
        this.handleDocDialogClose()
      }
    },
    '$route.query.planId': {
      immediate: false,
      handler(newPlanId) {
        if (newPlanId && newPlanId !== this.activePlanId) {
          this.selectPlan(newPlanId, true)
        }
      }
    }
  },
  methods: {
    async loadPlanList() {
      this.loadingPlans = true
      try {
        const res = await getMyTrainingPlanList()
        if (res.code === 1000) {
          this.planList = res.data || []
          if (this.planList.length > 0) {
            const queryPlanId = this.$route.query.planId || ''
            const matched = this.planList.find(item => String(item.id) === String(queryPlanId))
            const targetPlanId = matched ? matched.id : this.planList[0].id
            this.activePlanId = targetPlanId
            await this.loadPlanDetail(targetPlanId)
          } else {
            this.planDetail = null
          }
        } else {
          this.$message.error(res.message || '获取培训计划失败')
        }
      } catch (error) {
        console.error('loadPlanList error', error)
        this.$message.error('获取培训计划失败')
      } finally {
        this.loadingPlans = false
      }
    },
    async selectPlan(planId, force = false) {
      if (!planId) {
        return
      }
      if (!force && planId === this.activePlanId) {
        return
      }
      this.activePlanId = planId
      await this.loadPlanDetail(planId)
    },
    async loadPlanDetail(planId) {
      this.loadingDetail = true
      try {
        const res = await getMyTrainingPlanDetail({ planId })
        if (res.code === 1000) {
          this.planDetail = res.data || null
          this.tryAutoOpenTargetMaterial()
        } else {
          this.$message.error(res.message || '获取计划详情失败')
          this.planDetail = null
        }
      } catch (error) {
        console.error('loadPlanDetail error', error)
        this.$message.error('获取计划详情失败')
        this.planDetail = null
      } finally {
        this.loadingDetail = false
      }
    },
    getPlanTaskStats() {
      const stageList = this.planDetail && Array.isArray(this.planDetail.stageList) ? this.planDetail.stageList : []
      const stats = {
        total: 0,
        completed: 0,
        pending: 0,
        locked: 0
      }
      stageList.forEach(stage => {
        const isLocked = !stage || String(stage.stageStatus || '0') === '0'
        const materialList = Array.isArray(stage && stage.materialList) ? stage.materialList : []
        const hasExplicitRequired = materialList.some(rel => rel && rel.requiredFlag !== '0')
        materialList.forEach(rel => {
          if (!rel) {
            return
          }
          const isRequired = hasExplicitRequired ? rel.requiredFlag !== '0' : true
          if (!isRequired) {
            return
          }
          stats.total += 1
          if (isLocked) {
            stats.locked += 1
          } else if (String(rel.userStatus || '0') === '1') {
            stats.completed += 1
          } else {
            stats.pending += 1
          }
        })
        if (this.stageHasExam(stage) && stage.requiredPass !== '0') {
          stats.total += 1
          if (isLocked) {
            stats.locked += 1
            return
          }
          if (this.isStageExamPassed(stage)) {
            stats.completed += 1
          } else {
            stats.pending += 1
          }
        }
      })
      return stats
    },
    stageHasExam(stage) {
      const stageType = stage && stage.stageType ? stage.stageType : ''
      return !!(stage && stage.examId) && (stageType === 'resource_exam' || stageType === 'exam_only')
    },
    getStageTypeLabel(stageType) {
      if (stageType === 'resource_only') return '仅资源'
      if (stageType === 'resource_exam') return '资源+考试'
      if (stageType === 'exam_only') return '仅考试'
      return stageType || '-'
    },
    getStageStatusLabel(status) {
      if (status === '0') return '未解锁'
      if (status === '1') return '进行中'
      if (status === '2') return '已完成'
      return '未知'
    },
    getStageStatusType(status) {
      if (status === '0') return 'info'
      if (status === '1') return 'warning'
      if (status === '2') return 'success'
      return ''
    },
    getPlanStatusLabel(plan) {
      if (!plan) {
        return '进行中'
      }
      if (plan.planUserStatus === '1' && plan.planOverdueFlag === '1') {
        return '逾期完成'
      }
      if (plan.planUserStatus === '1') {
        return '已完成'
      }
      if (plan.planOverdueFlag === '1') {
        return '已逾期'
      }
      return '进行中'
    },
    parseDateTime(value) {
      if (!value) {
        return null
      }
      const normalized = String(value).replace(/-/g, '/')
      const date = new Date(normalized)
      return Number.isNaN(date.getTime()) ? null : date
    },
    formatDateTime(value) {
      const date = value instanceof Date ? value : this.parseDateTime(value)
      if (!date) {
        return '-'
      }
      const yyyy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      const hh = String(date.getHours()).padStart(2, '0')
      const mi = String(date.getMinutes()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
    },
    isPlanExpired(plan) {
      return !!(plan && plan.planOverdueFlag === '1' && plan.planUserStatus !== '1')
    },
    getPlanExpiredTip(plan) {
      if (!plan) {
        return '培训计划已逾期，当前未完成项已不可继续完成'
      }
      if (plan.planDeadlineTime) {
        return `培训计划已于 ${this.formatDateTime(plan.planDeadlineTime)} 截止，当前未完成项已不可继续完成`
      }
      return '培训计划已逾期，当前未完成项已不可继续完成'
    },
    isMaterialActionDisabled(stage, materialRel) {
      if (!stage) {
        return true
      }
      if (stage.stageStatus === '0') {
        return true
      }
      if (!this.isPlanExpired(this.planDetail)) {
        return false
      }
      return String(materialRel && materialRel.userStatus ? materialRel.userStatus : '0') !== '1'
    },
    isExamActionDisabled(stage) {
      if (!stage) {
        return true
      }
      if (stage.stageStatus === '0') {
        return true
      }
      if (!this.isPlanExpired(this.planDetail)) {
        return false
      }
      return !this.hasExamAttempt(stage)
    },
    getMaterialId(materialRel) {
      if (!materialRel) return ''
      return materialRel.materialId || (materialRel.material && materialRel.material.id) || ''
    },
    getMaterialVideoId(materialRel) {
      if (!materialRel || !materialRel.material || !materialRel.material.videoId) return ''
      return String(materialRel.material.videoId)
    },
    isTargetMaterial(materialRel) {
      return String(this.getMaterialId(materialRel)) === String(this.selectedMaterialId || '')
    },
    getMaterialTitle(materialRel) {
      return (materialRel.material && materialRel.material.title) || '未命名资料'
    },
    getMaterialType(materialRel) {
      return (materialRel.material && materialRel.material.fileType) || '-'
    },
    getFileUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      return this.$store.state.HOST + path
    },
    getMaterialExt(materialRel) {
      const material = (materialRel && materialRel.material) || {}
      const ext = (material.fileType || '').toLowerCase()
      if (ext) return ext
      const fileUrl = material.fileUrl || ''
      const clean = fileUrl.split('?')[0]
      const idx = clean.lastIndexOf('.')
      return idx >= 0 ? clean.slice(idx + 1).toLowerCase() : ''
    },
    isVideoExt(ext) {
      return ['mp4', 'mov', 'm3u8', 'avi', 'wmv', 'flv', 'mkv', 'webm'].includes(ext)
    },
    getPdfViewerUrl(url) {
      if (!url) return ''
      return url.includes('#') ? url : `${url}#toolbar=1&navpanes=0`
    },
    async recordMaterialView(materialId) {
      if (!materialId) {
        return
      }
      try {
        await recordApeTrainingMaterialView({ id: materialId })
      } catch (error) {
        console.error('记录培训资料查阅次数失败:', error)
      }
    },
    tryAutoOpenTargetMaterial() {
      const query = this.$route.query || {}
      if (query.autoOpen !== '1') {
        return
      }
      if (!this.planDetail || !this.planDetail.id) {
        return
      }
      const queryPlanId = query.planId || this.activePlanId
      if (queryPlanId && String(queryPlanId) !== String(this.planDetail.id)) {
        return
      }
      const materialId = query.materialId || ''
      if (!materialId) {
        return
      }
      const stageId = query.stageId || ''
      const key = `${this.planDetail.id}:${stageId}:${materialId}`
      if (key === this.autoOpenedTargetKey) {
        return
      }
      const stageList = this.planDetail.stageList || []
      let targetStage = null
      let targetMaterial = null

      for (let i = 0; i < stageList.length; i += 1) {
        const stage = stageList[i]
        if (stageId && String(stage.id) !== String(stageId)) {
          continue
        }
        const materialList = stage.materialList || []
        const hit = materialList.find(item => String(this.getMaterialId(item)) === String(materialId))
        if (hit) {
          targetStage = stage
          targetMaterial = hit
          break
        }
      }
      if (!targetStage || !targetMaterial) {
        return
      }
      if (targetStage.stageStatus === '0') {
        this.$message.warning(targetStage.lockReason || '该章节尚未解锁，请先完成解锁条件')
        return
      }

      this.autoOpenedTargetKey = key
      this.previewMaterial(targetStage, targetMaterial)
    },
    previewMaterial(stage, materialRel) {
      if (stage.stageStatus === '0') {
        this.$message.warning(stage.lockReason || '该章节尚未解锁，请按章节顺序学习')
        return
      }
      if (this.isMaterialActionDisabled(stage, materialRel)) {
        this.$message.warning(this.getPlanExpiredTip(this.planDetail))
        return
      }
      const material = materialRel.material || {}
      const fileUrl = material.fileUrl
      if (!fileUrl) {
        this.$message.warning('当前资料暂无可预览文件')
        return
      }
      const ext = this.getMaterialExt(materialRel)
      if (this.isVideoExt(ext)) {
        this.$router.push({
          path: '/videoPlay',
          query: {
            id: this.getMaterialVideoId(materialRel),
            from: 'training',
            fileUrl,
            title: this.getMaterialTitle(materialRel),
            planId: this.planDetail.id,
            stageId: stage.id,
            materialId: this.getMaterialId(materialRel)
          }
        })
        return
      }
      this.openDocLearningDialog(stage, materialRel)
    },
    openDocLearningDialog(stage, materialRel) {
      const materialId = this.getMaterialId(materialRel)
      if (!this.planDetail || !stage || !materialId) {
        this.$message.warning('资料信息不完整')
        return
      }
      const material = materialRel.material || {}
      if (!material.fileUrl) {
        this.$message.warning('当前资料暂无可预览文件')
        return
      }
      this.docLearningContext = {
        planId: this.planDetail.id,
        stageId: stage.id,
        materialId
      }
      this.docStaySeconds = 0
      this.docReachedBottom = false
      this.docBottomCheckReady = false
      this.pdfLoading = false
      this.pdfRenderError = ''
      this.currentPdfUrl = this.getFileUrl(material.fileUrl)
      this.docDialogVisible = true
      this.recordMaterialView(materialId)
    },
    handlePdfViewerStatus(status) {
      this.pdfLoading = !!(status && status.loading)
      this.pdfRenderError = (status && status.error) || ''
      this.docBottomCheckReady = !!(status && status.ready)
      this.docReachedBottom = !!(status && status.reachedBottom)
    },
    startDocStayTimer() {
      this.clearDocStayTimer()
      this.docStayTimer = setInterval(() => {
        this.docStaySeconds += 1
      }, 1000)
    },
    clearDocStayTimer() {
      if (this.docStayTimer) {
        clearInterval(this.docStayTimer)
        this.docStayTimer = null
      }
    },
    clearDocBottomCheckTimer() {
      this.docBottomCheckTimer = null
    },
    disconnectDocBottomObserver() {
      this.docBottomObserver = null
    },
    bindDocBottomObserver() {
      return null
    },
    checkDocBottomReached() {
      return false
    },
    handleDocScroll(event) {
      return event
    },
    async finishDocLearning() {
      if (this.docCompleting) {
        return
      }
      if (this.pdfLoading || !this.docBottomCheckReady) {
        this.$message.info('文档仍在加载中，请稍候再试')
        return
      }
      if (this.pdfRenderError) {
        this.$message.warning(this.pdfRenderError)
        return
      }
      if (this.docRemainingSeconds > 0 && !this.docReachedBottom) {
        this.$message.warning(`请继续阅读并滚动到文档末尾，系统会自动检测；再停留 ${this.docRemainingSeconds} 秒后可完成学习`)
        return
      }
      if (this.docRemainingSeconds > 0) {
        this.$message.warning(`请继续停留 ${this.docRemainingSeconds} 秒后再完成学习`)
        return
      }
      if (!this.docReachedBottom) {
        this.$message.warning('请继续阅读到文档末尾，系统检测到后会自动允许完成学习')
        return
      }
      const { planId, stageId, materialId } = this.docLearningContext || {}
      if (!planId || !stageId || !materialId) {
        this.$message.warning('学习上下文丢失，请重新打开资料')
        return
      }
      this.docCompleting = true
      try {
        const res = await completeTrainingMaterial({
          planId,
          stageId,
          materialId
        })
        if (res.code === 1000) {
          this.$message.success('资料学习完成，已满足解锁条件')
          this.docDialogVisible = false
          await this.loadPlanDetail(planId)
          await this.loadPlanList()
        } else {
          this.$message.error(res.message || '完成学习失败')
        }
      } catch (error) {
        console.error('finishDocLearning error', error)
        this.$message.error('完成学习失败')
      } finally {
        this.docCompleting = false
      }
    },
    handleDocDialogClose() {
      this.clearDocStayTimer()
      this.clearDocBottomCheckTimer()
      this.docCompleting = false
      this.docStaySeconds = 0
      this.docReachedBottom = false
      this.docBottomCheckReady = false
      this.pdfLoading = false
      this.pdfRenderError = ''
      this.currentPdfUrl = ''
      this.docLearningContext = {
        planId: '',
        stageId: '',
        materialId: ''
      }
    },
    toNumberOrNull(value) {
      if (value === null || value === undefined) {
        return null
      }
      if (typeof value === 'string' && value.trim() === '') {
        return null
      }
      const num = Number(value)
      return Number.isNaN(num) ? null : num
    },
    hasExamAttempt(stage) {
      if (!stage) {
        return false
      }
      if (stage.examSubmitted === true) {
        return true
      }
      return this.toNumberOrNull(stage.userExamScore) !== null
    },
    isStageExamFinalScoreReady(stage) {
      return !stage || stage.examFinalScoreReady !== false
    },
    isStageExamPendingReview(stage) {
      return this.hasExamAttempt(stage) && !this.isStageExamFinalScoreReady(stage)
    },
    isStageExamPassed(stage) {
      const currentScore = this.toNumberOrNull(stage && stage.userExamScore)
      const passScore = Number((stage && stage.passScore) || 60)
      return this.isStageExamFinalScoreReady(stage) && currentScore !== null && currentScore >= passScore
    },
    formatStageExamScore(stage) {
      const currentScore = this.toNumberOrNull(stage && stage.userExamScore)
      if (!this.hasExamAttempt(stage) || currentScore === null) {
        return '暂无'
      }
      if (this.isStageExamPendingReview(stage)) {
        return `${currentScore} 分（临时）`
      }
      return `${currentScore} 分`
    },
    formatStageExamReviewStatus(stage) {
      if (!this.hasExamAttempt(stage)) {
        return '未提交'
      }
      if (this.isStageExamPendingReview(stage)) {
        return `待管理员阅卷（剩余 ${Number(stage && stage.examPendingReviewCount ? stage.examPendingReviewCount : 0)} 题）`
      }
      return (stage && stage.examReviewStatus) || '无需阅卷'
    },
    getExamActionText(stage) {
      return this.hasExamAttempt(stage) ? '查看结果' : '进入考试'
    },
    toExam(stage) {
      if (stage.stageStatus === '0') {
        this.$message.warning(stage.lockReason || '该阶段考试尚未解锁')
        return
      }
      if (this.isExamActionDisabled(stage)) {
        this.$message.warning(this.getPlanExpiredTip(this.planDetail))
        return
      }
      if (!stage.examId) {
        this.$message.warning('该阶段未绑定考试')
        return
      }
      if (this.hasExamAttempt(stage)) {
        this.$router.push({
          path: '/testResult',
          query: {
            id: stage.examId,
            fromTask: '1',
            passScore: stage.passScore || 60
          }
        })
        return
      }
      this.$router.push({
        path: '/testInfo',
        query: {
          id: stage.examId,
          autoStart: '1',
          fromTask: '1',
          planId: this.planDetail && this.planDetail.id ? this.planDetail.id : '',
          stageId: stage.id,
          passScore: stage.passScore || 60
        }
      })
    }
  }
}
</script>

<style scoped>
.training-plan-guide-page {
  min-height: 100vh;
  background: #f3f5f8;
}

.guide-content {
  max-width: 1560px;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
}

.plan-deadline-alert {
  margin-bottom: 16px;
}

.plan-sidebar {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8edf4;
  padding: 14px;
  height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 80px;
  overflow-y: auto;
}

.sidebar-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2d3d;
  margin-bottom: 10px;
}

.plan-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 240px;
  position: relative;
}

.plan-item {
  border: 1px solid #e7ecf2;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.plan-item:hover {
  border-color: #409eff;
}

.plan-item.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.plan-name {
  font-size: 14px;
  font-weight: 700;
  color: #203040;
}

.plan-meta {
  margin-top: 6px;
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #7a8b9a;
}

.plan-detail {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8edf4;
  padding: 18px;
  min-height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
}

.plan-detail-body {
  flex: 1;
  min-height: 360px;
  width: 100%;
  position: relative;
}

::v-deep .plan-list > .el-loading-mask,
::v-deep .plan-detail-body > .el-loading-mask {
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .plan-list > .el-loading-mask .el-loading-spinner,
::v-deep .plan-detail-body > .el-loading-mask .el-loading-spinner {
  position: static;
  top: auto;
  margin-top: 0;
  width: auto;
  transform: none;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #edf1f5;
}

.detail-title {
  margin: 0;
  font-size: 24px;
  color: #172b4d;
}

.detail-desc {
  margin-top: 8px;
  color: #6b7c93;
  font-size: 13px;
}

.detail-metrics {
  display: flex;
  gap: 10px;
}

.metric-card {
  min-width: 160px;
  background: #f7f9fc;
  border-radius: 8px;
  padding: 10px 12px;
}

.metric-label {
  font-size: 12px;
  color: #7a8b9a;
}

.metric-value {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #203040;
}

.guide-stats {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.guide-stat-card {
  border-radius: 12px;
  padding: 16px 18px;
  border: 1px solid #e7ecf2;
  background: #fff;
}

.guide-stat-card__label {
  font-size: 13px;
  color: #7a8b9a;
}

.guide-stat-card__value {
  margin-top: 10px;
  font-size: 28px;
  line-height: 1;
  font-weight: 700;
  color: #203040;
}

.guide-stat-card--actionable {
  background: linear-gradient(135deg, #eef6ff 0%, #f8fbff 100%);
  border-color: #dce8ff;
}

.guide-stat-card--actionable .guide-stat-card__value {
  color: #2f67ff;
}

.guide-stat-card--completed {
  background: linear-gradient(135deg, #eefaf2 0%, #f7fcf9 100%);
  border-color: #d8f0df;
}

.guide-stat-card--completed .guide-stat-card__value {
  color: #1f9d55;
}

.guide-stat-card--locked {
  background: linear-gradient(135deg, #fff7ed 0%, #fffdf8 100%);
  border-color: #fde7cf;
}

.guide-stat-card--locked .guide-stat-card__value {
  color: #d97706;
}

.guide-stat-card--total {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
}

.stage-list {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.stage-card {
  border: 1px solid #e7ecf2;
  border-radius: 10px;
  padding: 12px;
}

.stage-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stage-title {
  font-size: 16px;
  color: #172b4d;
  font-weight: 700;
}

.stage-sub {
  margin-top: 6px;
  font-size: 12px;
  color: #7a8b9a;
}

.stage-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.lock-reason {
  margin-top: 10px;
  padding: 8px 10px;
  background: #fff7e6;
  color: #d48806;
  border-radius: 6px;
  font-size: 12px;
}

.material-block,
.exam-block {
  margin-top: 12px;
}

.block-title {
  font-size: 13px;
  font-weight: 700;
  color: #203040;
  margin-bottom: 8px;
}

.block-empty {
  color: #99a5b4;
  font-size: 12px;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.material-item {
  border: 1px solid #edf1f5;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.material-item.highlight {
  border-color: #1a71ff;
  background: #eef4ff;
}

.material-title {
  font-size: 14px;
  color: #203040;
  font-weight: 600;
}

.material-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #7a8b9a;
  display: flex;
  gap: 8px;
}

.required-mark {
  color: #f56c6c;
}

.material-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.exam-row {
  border: 1px solid #edf1f5;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.exam-review-tip {
  margin-top: 8px;
  color: #7a8b9a;
  font-size: 12px;
  line-height: 1.7;
}

.doc-header-tip {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f3f7ff;
  color: #3a4a5c;
  font-size: 13px;
}

::v-deep .pdf-fullscreen-dialog {
  margin: 0 !important;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

::v-deep .pdf-fullscreen-dialog .el-dialog__header {
  flex-shrink: 0;
}

::v-deep .pdf-fullscreen-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 0;
}

::v-deep .pdf-fullscreen-dialog .el-dialog__footer {
  flex-shrink: 0;
  padding-top: 12px;
}

.doc-tip-main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.doc-tip-main i {
  color: #409eff;
  font-size: 15px;
}

.doc-progress-tags {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.doc-progress-chip {
  padding: 4px 10px;
  border-radius: 999px;
  background: #e8f1ff;
  color: #3d5a80;
  line-height: 20px;
  white-space: nowrap;
}

.doc-progress-chip.done {
  background: #e8f8ee;
  color: #1f8b4c;
}

.doc-viewer-wrap {
  width: 100%;
  flex: 1;
  min-height: 0;
  height: calc(100vh - 210px);
  overflow: hidden;
  background: #111827;
  border-radius: 10px;
  border: 1px solid #263142;
  padding: 0;
  box-sizing: border-box;
}

.doc-bottom-confirm {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e5ecf5;
  border-radius: 8px;
  background: #f8fbff;
}

.doc-bottom-confirm-tip {
  color: #6b7c93;
  font-size: 12px;
}

.doc-finish-btn {
  min-width: 180px;
}

@media (max-width: 992px) {
  .guide-content {
    grid-template-columns: 1fr;
  }
  .plan-sidebar {
    height: auto;
    position: static;
  }
  .detail-header {
    flex-direction: column;
  }
  .detail-metrics {
    flex-wrap: wrap;
  }
  .guide-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .doc-header-tip {
    flex-direction: column;
    align-items: flex-start;
  }
  .doc-progress-tags {
    justify-content: flex-start;
  }
  .doc-viewer-wrap {
    height: calc(100vh - 260px);
  }
  .doc-bottom-confirm {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .guide-stats {
    grid-template-columns: 1fr;
  }
}
</style>
